import { applyChanges } from '@components/bid_request/details/utils';
import { keepEqualHeaders } from '@features/buyer-line-items-pagination/utils/keep-equal-headers';
import type { ID, Lot, LotChanges } from 'types';

interface RemergeLotParams {
  changes: Record<ID, LotChanges>;
  lotId: ID;
  lots?: Lot[];
  originalLots: Lot[];
}

const mergeOne = (changes: Record<ID, LotChanges>, lotId: ID, originalLot?: Lot): Lot | undefined => {
  const lotChanges = changes[lotId];
  if (!originalLot) return lotChanges ? applyChanges({ lotChanges: { [lotId]: lotChanges }, lots: [] })[0] : undefined;

  return applyChanges({ lotChanges: lotChanges ? { [lotId]: lotChanges } : undefined, lots: [originalLot] })[0];
};

/**
 * Rebuilds one lot's merged copy from its loaded rows (none for an unsaved `new-` lot) and its overlay entry,
 * leaving every other lot the same object, so one lot's page or edit does not hand every mounted grid new
 * props. The lot keeps its previous `custom_column_headers` array when the headers are unchanged. A lot
 * marked for deletion leaves the list; a lot not in the list yet goes through `applyChanges` for every lot,
 * which places it.
 */
export const remergeLot = ({ changes, lotId, lots, originalLots }: RemergeLotParams): Lot[] => {
  if (!lots) return applyChanges({ lotChanges: changes, lots: originalLots });

  const originalLot = originalLots.find(lot => String(lot.id) === String(lotId));
  const merged = mergeOne(changes, lotId, originalLot);
  const index = lots.findIndex(lot => String(lot.id) === String(lotId));

  if (!merged) return index === -1 ? lots : lots.filter((_, i) => i !== index);
  if (index === -1) return applyChanges({ lotChanges: changes, lots: originalLots });

  const next = [...lots];
  next[index] = keepEqualHeaders(lots[index], merged);

  return next;
};
